import type { FlowAPI } from '@wix/yoshi-flow-editor';

// Tells apart editorReady runs that share this module (and its state) from runs in another worker.
export const editorWorkerId = Math.random().toString(36).slice(2, 10);

let panoramaClient: FlowAPI['panoramaClient'];
let panoramaErrorMonitor:
  | ReturnType<NonNullable<FlowAPI['panoramaClient']>['errorMonitor']>
  | undefined;

type Options = {
  metaSiteId: string;
  instanceId: string;
  panoramaClient: FlowAPI['panoramaClient'];
};

export const initMonitoring = (options: Options) => {
  if (options.panoramaClient) {
    panoramaClient = options.panoramaClient;
    panoramaErrorMonitor = options.panoramaClient.errorMonitor({
      data: { tags: { metaSiteId: options.metaSiteId, instanceId: options.instanceId } },
    });
  }
};

export const reportError = (error: unknown) => {
  if (typeof error === 'string') {
    return panoramaErrorMonitor?.reportError(new Error(error));
  } else if (error instanceof Error) {
    return panoramaErrorMonitor?.reportError(error);
  }
};

export const toMonitored = async (
  interactionName: string,
  promise: Promise<any>,
  rethrow: boolean = true,
  timeout?: number,
) => {
  let isResolved = false;
  let clearTimer = () => {};

  try {
    interactionStarted(interactionName);

    if (timeout) {
      const timeoutId = setTimeout(() => {
        interactionFailed(interactionName, new Error(`Timeout exceeded: "${interactionName}"`));
        isResolved = true;
      }, timeout);
      clearTimer = () => clearTimeout(timeoutId);
    }

    const response = await promise;

    if (!isResolved) {
      interactionEnded(interactionName);
    }

    return response;
  } catch (err) {
    if (!isResolved) {
      interactionFailed(interactionName, err instanceof Error ? err : new Error(String(err)));
    }
    if (rethrow) {
      throw err;
    }
  } finally {
    clearTimer();
  }
};

export const log = (message: string, data?: Record<string, any>) => {
  panoramaClient?.logger().info(message, data);
};

/**
 * Viewer-safe variant of `toMonitored` that accepts a `panoramaClient` directly,
 * instead of relying on the module-level singleton set by `initMonitoring` (which
 * is only called from the editor bundle). Use this for viewer controllers where
 * `flowAPI.panoramaClient` is the source of truth.
 */
export async function toMonitoredViewer<T>(
  client: FlowAPI['panoramaClient'],
  interactionName: string,
  promiseFn: () => Promise<T>,
): Promise<T>;
export async function toMonitoredViewer<T>(
  client: FlowAPI['panoramaClient'],
  interactionName: string,
  promiseFn: () => Promise<T>,
  rethrow: false,
): Promise<T | undefined>;
export async function toMonitoredViewer<T>(
  client: FlowAPI['panoramaClient'],
  interactionName: string,
  promiseFn: () => Promise<T>,
  rethrow: boolean = true,
): Promise<T | undefined> {
  const tx = client?.transaction(interactionName);
  tx?.start();

  try {
    const response = await promiseFn();
    tx?.finish();
    return response;
  } catch (err) {
    const error = err instanceof Error ? err : new Error(String(err));
    client?.logger().error(`${interactionName}: ${error.message}`, { interactionName });
    client?.errorMonitor().reportError(error, {
      tags: { interactionName, fedopsError: 'interactionFailed' },
    });
    if (rethrow) {
      throw err;
    }
    return undefined;
  }
}

const interactionStarted = (interactionName: string) => {
  try {
    return panoramaClient?.transaction(interactionName).start();
  } catch (err) {
    if (err instanceof Error) {
      panoramaErrorMonitor?.reportError(err, {
        tags: { interactionName, fedopsError: 'interactionStarted' },
      });
    }
  }
};

const interactionEnded = (interactionName: string) => {
  try {
    return panoramaClient?.transaction(interactionName).finish();
  } catch (err) {
    if (err instanceof Error) {
      panoramaErrorMonitor?.reportError(err, {
        tags: { interactionName, fedopsError: 'interactionEnded' },
      });
    }
  }
};

const interactionFailed = (interactionName: string, err: Error, message?: string) => {
  panoramaClient?.logger().error(`${interactionName}${message ? ': ' + message : ''}`, err);

  panoramaErrorMonitor?.reportError(err, {
    tags: { interactionName, fedopsError: 'interactionFailed' },
  });
};
